import React, { useState, useEffect, useCallback } from "react";
import {
  FaUsers,
  FaPlus,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaCalendarAlt,
  FaRobot,
  FaTimes,
} from "react-icons/fa";
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Input,
  InputGroup,
  Select,
  Spinner,
  EmptyState,
} from "../ui";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/api";
import { useModal } from "../../context/ModalContext";

const INTERVAL_UNITS = ["minutes", "hours", "days", "weeks"];

const emptyForm = {
  name: "",
  sessionId: "",
  groupId: "",
  groupName: "",
  message: "",
  mediaUrl: "",
  mode: "scheduled",
  intervalValue: 24,
  intervalUnit: "hours",
  maxIterations: 0,
  triggerKeywords: "",
  aiPrompt: "",
};

function KeywordInput({ value, onChange, placeholder }) {
  const list = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const add = () => {
    const cleaned = draft.trim();
    if (!cleaned) return;
    if (!list.includes(cleaned)) onChange([...list, cleaned]);
    setDraft("");
  };
  return (
    <div>
      <div className="input flex flex-wrap gap-1.5 min-h-[40px] items-center">
        {list.map((kw, i) => (
          <span key={`${kw}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-xs font-medium">
            {kw}
            <button type="button" className="hover:text-red-500" onClick={() => onChange(list.filter((_, idx) => idx !== i))}>
              <FaTimes size={8} />
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[80px] outline-none border-none bg-transparent text-sm py-0.5"
          placeholder={list.length === 0 ? placeholder : ""}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
        />
      </div>
      <p className="input-hint">If any keyword matches a group message, the AI replies. Empty = reply to every message.</p>
    </div>
  );
}

export default function GroupFollowUps({ sessions = [] }) {
  const modal = useModal();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("/api/group-follow-ups");
      setRows(res?.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (!form.sessionId) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    setGroupsLoading(true);
    apiGet(`/api/groups/${form.sessionId}`)
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res) ? res : res?.data || res?.groups || [];
        setGroups(Array.isArray(data) ? data : []);
      })
      .catch(() => !cancelled && setGroups([]))
      .finally(() => !cancelled && setGroupsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [form.sessionId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.sessionId || !form.groupId) {
      modal.alert("Name, session and group are required.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sessionId: form.sessionId,
        groupId: form.groupId,
        groupName: form.groupName,
        message: form.message,
        mediaUrl: form.mediaUrl,
        reactive: form.mode === "reactive",
        intervalValue: Number(form.intervalValue) || 24,
        intervalUnit: form.intervalUnit,
        maxIterations: Number(form.maxIterations) || 0,
        triggerKeywords: form.triggerKeywords.split(",").map((k) => k.trim()).filter(Boolean),
        aiPrompt: form.aiPrompt,
      };
      const res = await apiPost("/api/group-follow-ups", payload);
      if (res?.success) {
        setShowForm(false);
        setForm(emptyForm);
        fetchRows();
      } else {
        modal.alert(res?.message || "Failed to create follow-up", "error");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to create follow-up", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row) => {
    const res = await apiPut(`/api/group-follow-ups/${row.id}/toggle`, {});
    if (res?.success) fetchRows();
    else modal.alert(res?.message || "Failed to toggle", "error");
  };

  const handleDelete = async (row) => {
    if (!(await modal.confirm(`Delete "${row.name}"?`, { danger: true, confirmText: "Delete" }))) return;
    const res = await apiDelete(`/api/group-follow-ups/${row.id}`);
    if (res?.success) fetchRows();
  };

  const isActive = (row) => row.status === "scheduled" || row.status === "running";

  return (
    <Card className="mt-6">
      <CardHeader className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaUsers size={13} className="text-emerald-600" />
          <span className="font-semibold text-ink-primary text-sm">
            Group Follow-Ups {rows.length > 0 && `(${rows.length})`}
          </span>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowForm((s) => !s)}>
          <FaPlus size={10} className="mr-1.5" /> New Follow-Up
        </Button>
      </CardHeader>

      <CardBody>
        <p className="text-xs text-ink-muted mb-4">
          Post scheduled messages into WhatsApp groups, or let the AI reply in a group when
          messages match your keywords.
        </p>

        {/* Create form */}
        {showForm && (
          <form onSubmit={handleCreate} className="border border-surface-border rounded-lg p-4 mb-4 space-y-3 bg-surface-bg/40">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InputGroup label="Name">
                <Input
                  placeholder="e.g. Weekly product drop"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </InputGroup>
              <InputGroup label="WhatsApp Session">
                <Select
                  value={form.sessionId}
                  onChange={(e) => setForm((f) => ({ ...f, sessionId: e.target.value, groupId: "", groupName: "" }))}
                  required
                >
                  <option value="">Select a connected session</option>
                  {sessions.map((s) => (
                    <option key={s.sessionId} value={s.sessionId}>
                      {s.number || s.sessionId}
                    </option>
                  ))}
                </Select>
              </InputGroup>
            </div>

            <InputGroup label="Group">
              {!form.sessionId ? (
                <p className="text-xs text-ink-muted">Select a session first.</p>
              ) : groupsLoading ? (
                <div className="flex items-center gap-2 text-ink-muted text-sm py-1">
                  <Spinner size="sm" /> Loading groups…
                </div>
              ) : (
                <Select
                  value={form.groupId}
                  onChange={(e) => {
                    const g = groups.find((x) => x.id === e.target.value);
                    setForm((f) => ({ ...f, groupId: e.target.value, groupName: g?.name || "" }));
                  }}
                  required
                >
                  <option value="">Choose a group</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({g.size} members)
                    </option>
                  ))}
                </Select>
              )}
            </InputGroup>

            <InputGroup label="Mode">
              <div className="flex gap-2">
                <button
                  type="button"
                  className={
                    "flex-1 px-3 py-2 rounded-lg text-xs font-medium border flex items-center justify-center gap-1.5 " +
                    (form.mode === "scheduled"
                      ? "bg-emerald-500 text-white border-emerald-500"
                      : "border-surface-border text-ink-secondary")
                  }
                  onClick={() => setForm((f) => ({ ...f, mode: "scheduled" }))}
                >
                  <FaCalendarAlt size={10} /> Scheduled posts
                </button>
                <button
                  type="button"
                  className={
                    "flex-1 px-3 py-2 rounded-lg text-xs font-medium border flex items-center justify-center gap-1.5 " +
                    (form.mode === "reactive"
                      ? "bg-fuchsia-500 text-white border-fuchsia-500"
                      : "border-surface-border text-ink-secondary")
                  }
                  onClick={() => setForm((f) => ({ ...f, mode: "reactive" }))}
                >
                  <FaRobot size={10} /> AI replies
                </button>
              </div>
            </InputGroup>

            {form.mode === "scheduled" ? (
              <>
                <InputGroup label="Message">
                  <textarea
                    className="input min-h-[80px]"
                    placeholder="Type the message to post into the group..."
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  />
                </InputGroup>
                <div className="grid grid-cols-3 gap-3">
                  <InputGroup label="Every">
                    <Input
                      type="number"
                      min="1"
                      value={form.intervalValue}
                      onChange={(e) => setForm((f) => ({ ...f, intervalValue: e.target.value }))}
                    />
                  </InputGroup>
                  <InputGroup label="Unit">
                    <Select
                      value={form.intervalUnit}
                      onChange={(e) => setForm((f) => ({ ...f, intervalUnit: e.target.value }))}
                    >
                      {INTERVAL_UNITS.map((u) => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </Select>
                  </InputGroup>
                  <InputGroup label="Max sends" hint="0 = unlimited">
                    <Input
                      type="number"
                      min="0"
                      value={form.maxIterations}
                      onChange={(e) => setForm((f) => ({ ...f, maxIterations: e.target.value }))}
                    />
                  </InputGroup>
                </div>
                <InputGroup label="Media URL (optional)">
                  <Input
                    placeholder="https://example.com/image.jpg"
                    value={form.mediaUrl}
                    onChange={(e) => setForm((f) => ({ ...f, mediaUrl: e.target.value }))}
                  />
                </InputGroup>
              </>
            ) : (
              <>
                <InputGroup label="Trigger keywords">
                  <KeywordInput
                    value={form.triggerKeywords ? form.triggerKeywords.split(",").map((k) => k.trim()).filter(Boolean) : []}
                    onChange={(arr) => setForm((f) => ({ ...f, triggerKeywords: arr.join(",") }))}
                    placeholder="e.g. price, support (Enter to add)"
                  />
                </InputGroup>
                <InputGroup label="AI instructions">
                  <textarea
                    className="input min-h-[80px]"
                    placeholder="You are a helpful assistant answering questions in this group..."
                    value={form.aiPrompt}
                    onChange={(e) => setForm((f) => ({ ...f, aiPrompt: e.target.value }))}
                  />
                </InputGroup>
              </>
            )}

            <div className="flex items-center gap-2">
              <Button type="submit" variant="primary" size="sm" loading={saving}>
                Create
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center gap-2 text-ink-muted py-4">
            <Spinner size="sm" /> Loading follow-ups…
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={FaUsers}
            title="No group follow-ups yet"
            description="Create one to post scheduled messages into a group or have the AI answer group messages."
          />
        ) : (
          <div className="divide-y divide-surface-border">
            {rows.map((row) => (
              <div key={row.id} className="flex items-center justify-between py-3 gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-ink-primary truncate">{row.name}</p>
                    <Badge variant={row.reactive ? "info" : "brand"}>
                      {row.reactive ? "AI replies" : "Scheduled"}
                    </Badge>
                    <Badge variant={isActive(row) ? "success" : "neutral"}>
                      {isActive(row) ? "Active" : row.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-ink-muted truncate mt-0.5">
                    {row.groupName || row.groupId}
                    {!row.reactive && ` · every ${row.intervalValue} ${row.intervalUnit}`}
                    {row.reactive && Array.isArray(row.triggerKeywords) && row.triggerKeywords.length > 0
                      ? ` · keys: ${row.triggerKeywords.join(", ")}`
                      : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    className={`btn-sm flex items-center gap-1 ${isActive(row) ? "btn-secondary" : "btn-primary"}`}
                    onClick={() => handleToggle(row)}
                  >
                    {isActive(row) ? <><FaToggleOff size={11} /> Pause</> : <><FaToggleOn size={11} /> Resume</>}
                  </button>
                  <button className="btn-danger btn-sm btn-icon" onClick={() => handleDelete(row)}>
                    <FaTrash size={10} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
